<template>
  <div class="page-wrapper">
    <!-- Header -->
    <div class="page-header mb-7">
      <div class="header-accent"></div>
      <div class="header-content">
        <div class="d-flex align-center" style="gap: 14px">
          <div class="header-icon-wrap">
            <v-icon color="#00A63E" size="22"
              >mdi-clipboard-text-outline</v-icon
            >
          </div>
          <div>
            <div class="page-title">Student Evaluation</div>
            <div class="page-subtitle">
              View Student Performance Evaluations
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Filter Card -->
    <v-card flat class="filter-card pa-5 rounded-2xl mb-5">
      <div class="d-flex align-center flex-wrap" style="gap: 16px">
        <!-- Filter label -->
        <div class="filter-label-wrap d-flex align-center" style="gap: 6px">
          <v-icon color="#9ca3af" size="16">mdi-filter-variant</v-icon>
          <span class="filter-label">Filters</span>
        </div>

        <div class="filter-divider"></div>

        <!-- Program -->
        <div class="d-flex align-center" style="gap: 10px">
          <span class="filter-field-label">Program</span>
          <div style="width: 190px">
            <v-autocomplete
              density="compact"
              v-model="programs"
              item-value="id"
              item-title="name"
              :color="$vuetify.theme.themes.light.inputColor"
              variant="outlined"
              hide-details
              @update:modelValue="initialize"
              :items="programsList"
              class="custom-select"
            />
          </div>
        </div>

        <!-- Batch -->
        <div class="d-flex align-center" style="gap: 10px">
          <span class="filter-field-label">Batch</span>
          <div style="width: 190px">
            <v-autocomplete
              density="compact"
              v-model="batch"
              item-value="id"
              item-title="name"
              @update:modelValue="initialize"
              :color="$vuetify.theme.themes.light.inputColor"
              variant="outlined"
              hide-details
              :items="batchList"
              class="custom-select"
            />
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="tabs-wrap mt-5">
        <div class="tabs-row">
          <button
            v-for="tab in tabList"
            :key="tab.id"
            class="custom-tab"
            :class="{ active: activeTab === tab.name }"
            @click="changeTab(tab)"
          >
            <v-icon size="15" class="tab-icon">
              {{
                tab.id === 1 ? "mdi-check-circle-outline" : "mdi-clock-outline"
              }}
            </v-icon>
            {{ tab.name }}
          </button>
        </div>
      </div>
    </v-card>

    <!-- Data Table Card -->
    <v-card flat class="table-card rounded-2xl">
      <!-- Table header bar -->
      <div
        class="table-topbar px-5 py-4 d-flex align-center justify-space-between"
      >
        <div class="d-flex align-center" style="gap: 8px">
          <span class="table-title">
            {{
              activeTab === "Evaluated"
                ? "Evaluated Students"
                : "Pending Evaluation"
            }}
          </span>
          <span v-if="tbl_data.length > 0" class="count-badge">{{
            tbl_data.length
          }}</span>
        </div>
        <div style="width: 220px">
          <v-text-field
            v-model="search"
            density="compact"
            variant="outlined"
            hide-details
            placeholder="Search..."
            prepend-inner-icon="mdi-magnify"
            class="custom-search"
          />
        </div>
      </div>

      <v-divider />

      <v-data-table
        :headers="headers"
        :items="tbl_data"
        :search="search"
        :loading="loading"
        hover
        class="eval-table"
      >
        <!-- Loading state -->
        <template #loading>
          <div class="text-center py-10">
            <v-progress-circular indeterminate color="#00A63E" size="36" />
            <div class="mt-3 loading-text">Loading evaluations...</div>
          </div>
        </template>

        <!-- Name -->
        <template v-slot:[`item.student_name`]="{ item }">
          <div class="d-flex align-center" style="gap: 10px; padding: 6px 0">
            <div class="student-avatar">
              <span>{{
                tab == 1
                  ? (item.student?.fname?.[0] ?? "") +
                    (item.student?.lname?.[0] ?? "")
                  : (item.fname?.[0] ?? "") + (item.lname?.[0] ?? "")
              }}</span>
            </div>
            <div class="student-name">
              <span v-if="tab == 1">
                {{ item.student?.fname }} {{ item.student?.lname }}
              </span>
              <span v-else>{{ item.fname }} {{ item.lname }}</span>
            </div>
          </div>
        </template>

        <!-- Program -->
        <template v-slot:[`item.program`]="{ item }">
          <v-chip
            v-if="tab == 1"
            size="x-small"
            class="program-chip"
            variant="tonal"
            color="#00A63E"
          >
            {{ item.student?.program?.abbr }}
          </v-chip>
          <v-chip
            v-else
            size="x-small"
            class="program-chip"
            variant="tonal"
            color="#00A63E"
          >
            {{ item.program?.abbr }}
          </v-chip>
        </template>

        <!-- Evaluated By -->
        <template v-slot:[`item.eval_by`]="{ item }">
          <div v-if="tab == 1" class="eval-by-cell">
            <v-icon size="14" color="#9ca3af" class="mr-1"
              >mdi-account-outline</v-icon
            >
            {{
              item.field_officer_id
                ? item.evaluated_by_field_officer?.officer_name
                : item.evaluated_by_org?.head_of_agency
            }}
          </div>
          <span v-else class="not-available">—</span>
        </template>

        <!-- Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <v-btn
            v-if="tab == 1"
            size="small"
            class="view-btn"
            variant="tonal"
            prepend-icon="mdi-eye-outline"
            rounded="lg"
            @click="viewItem(item)"
          >
            View
          </v-btn>
        </template>

        <!-- No data -->
        <template #no-data>
          <div class="no-data-slot">
            <div class="no-data-icon-wrap">
              <v-icon size="36" color="#d1d5db">mdi-file-search-outline</v-icon>
            </div>
            <div class="no-data-title">No records found</div>
            <div class="no-data-sub">
              {{
                batch && programs
                  ? "No evaluations match the selected filters."
                  : "Please select a Program and Batch to load data."
              }}
            </div>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <fade-away-message-component
      displayType="default"
      v-model="fadeAwayMessage.show"
      :message="fadeAwayMessage.message"
      :header="fadeAwayMessage.header"
      :type="fadeAwayMessage.type"
    />
  </div>
</template>

<script>
import eventBus from "@/eventBus";

export default {
  data: () => ({
    search: "",
    activeTab: "Evaluated",
    tab: 1,
    tabList: [
      { id: 1, name: "Evaluated" },
      { id: 2, name: "Not Evaluated" },
    ],
    headers: [
      { title: "Name", value: "student_name", align: "start" },
      { title: "Program", value: "program", align: "start" },
      { title: "Organization", value: "student.org.name", align: "start" },
      { title: "Evaluated By", value: "eval_by", align: "start" },
      {
        title: "Actions",
        value: "actions",
        align: "center",
        sortable: false,
        width: 160,
      },
    ],
    data: [],
    perPageChoices: [
      { text: "5", value: 5 },
      { text: "10", value: 10 },
      { text: "20", value: 20 },
      { text: "50", value: 50 },
      { text: "100", value: 100 },
    ],
    totalCount: 0,
    itemData: [],
    tbl_data: [],
    programs: null,
    programsList: [],
    alerted: false,
    batchList: [],
    batch: null,
    loading: false,
    options: {},
    action: null,
    paginationData: {},
    fadeAwayMessage: {
      show: false,
      type: "success",
      header: "Successfully Deleted!",
      message: "",
      top: 10,
    },
  }),

  mounted() {
    this.getPrograms();
    this.getBatch();
    eventBus.on("closeStudentRequirementsDialog", () => {
      this.initialize();
    });
  },

  beforeUnmount() {
    eventBus.off("closeStudentRequirementsDialog");
  },

  methods: {
    pagination(data) {
      this.paginationData = data;
    },

    initialize() {
      if (this.batch && this.programs) {
        this.loading = true;
        this.axiosCall(
          "/student/getStudentEvaluation/" +
            this.programs +
            "/" +
            this.batch +
            "/" +
            this.tab,
          "GET",
        ).then((res) => {
          if (res) {
            this.tbl_data = res.data;
            this.loading = false;
          }
        });
      }
    },

    viewItem(item) {
      let url =
        process.env.VUE_APP_SERVER +
        `/pdfs/evaluation/${item.student.program.institute.abbr}/${item.id}/${item.student.id}`;
      let token = localStorage.getItem("token");
      const tab = window.open("about:blank");
      this.isLoading = true;
      fetch(url, { headers: { Authorization: `Bearer ${token}` } })
        .then((response) => response.blob())
        .then((blob) => {
          var _url = window.URL.createObjectURL(blob);
          tab.location = _url;
          tab.focus();
          this.isLoading = false;
        })
        .catch((err) => {
          console.log(err);
        });
    },

    getPrograms() {
      this.axiosCall("/program/getProgramsByInstitute", "GET").then((res) => {
        this.programsList = res.data;
        this.programsList.push({ id: -1, name: "All Programs" });
        this.programs = this.programsList[this.programsList.length - 1].id;
      });
    },

    getBatch() {
      this.axiosCall("/program/getProgramsBatchByInstitute", "GET").then(
        (res) => {
          this.batchList = res.data;
        },
      );
    },

    changeTab(tab) {
      this.activeTab = tab.name;
      this.tab = tab.id;
      this.tbl_data = [];
      this.initialize();
    },

    addnew() {
      this.itemData = [{ id: null }];
      this.action = "Add";
    },
    upload() {
      this.uploadData = { id: null };
      this.action = "Add";
    },
    // deleteItem(item) {
    //   this.alerted = true;
    // },
    editItem(item) {
      this.itemData = item;
      this.action = "Update";
    },
  },
};
</script>

<style scoped>
/* ─── Page wrapper ─── */
.page-wrapper {
  padding: 4px;
  font-family: "DM Sans", "Nunito", sans-serif;
}

/* ─── Header ─── */
.page-header {
  position: relative;
  padding-left: 18px;
}
.header-accent {
  position: absolute;
  left: 0;
  top: 4px;
  bottom: 4px;
  width: 4px;
  border-radius: 4px;
  background: linear-gradient(180deg, #00a63e 0%, #00c853 100%);
}
.header-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.page-title {
  font-size: 26px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.5px;
  line-height: 1.1;
}
.page-subtitle {
  font-size: 13px;
  color: #6b7280;
  margin-top: 3px;
  font-weight: 400;
}

/* ─── Filter card ─── */
.filter-card {
  border: 1px solid #e5e7eb !important;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.05) !important;
  background: #fff !important;
}
.filter-label {
  font-size: 12.5px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.filter-divider {
  width: 1px;
  height: 20px;
  background: #e5e7eb;
}
.filter-field-label {
  font-size: 13px;
  color: #374151;
  font-weight: 600;
  white-space: nowrap;
}

/* ─── Custom tabs ─── */
.tabs-wrap {
  border-top: 1px solid #f3f4f6;
  padding-top: 16px;
}
.tabs-row {
  display: flex;
  gap: 4px;
}
.custom-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 18px;
  border-radius: 999px;
  border: 1.5px solid transparent;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  background: transparent;
  transition: all 0.18s ease;
  letter-spacing: 0.1px;
}
.custom-tab:hover {
  background: #f9fafb;
  color: #374151;
}
.custom-tab.active {
  background: #f0fdf4;
  color: #00a63e;
  border-color: #bbf7d0;
}
.tab-icon {
  opacity: 0.75;
}

/* ─── Table card ─── */
.table-card {
  border: 1px solid #e5e7eb !important;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.05) !important;
  background: #fff !important;
  overflow: hidden;
}
.table-topbar {
  background: #fafafa;
  border-bottom: 1px solid #f3f4f6;
}
.table-title {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
}
.count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  border-radius: 999px;
  background: #dcfce7;
  color: #166534;
  font-size: 11px;
  font-weight: 700;
}

/* ─── Student cell ─── */
.student-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  border: 1.5px solid #bbf7d0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: #065f46;
  text-transform: uppercase;
  flex-shrink: 0;
}
.student-name {
  font-size: 13.5px;
  font-weight: 600;
  color: #111827;
}

/* ─── Program chip ─── */
.program-chip {
  font-weight: 700 !important;
  font-size: 11px !important;
  letter-spacing: 0.3px;
}

/* ─── Eval by ─── */
.eval-by-cell {
  font-size: 13px;
  color: #374151;
  display: flex;
  align-items: center;
}
.not-available {
  color: #d1d5db;
  font-size: 16px;
}

/* ─── View btn ─── */
.view-btn {
  background: #f0fdf4 !important;
  color: #00a63e !important;
  border: 1.5px solid #bbf7d0 !important;
  font-weight: 600 !important;
  font-size: 12.5px !important;
  letter-spacing: 0.2px;
  transition: all 0.15s ease !important;
}
.view-btn:hover {
  background: #dcfce7 !important;
  border-color: #86efac !important;
}

/* ─── No data ─── */
.no-data-slot {
  padding: 56px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.no-data-icon-wrap {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: #f9fafb;
  border: 1px solid #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
}
.no-data-title {
  font-size: 15px;
  font-weight: 700;
  color: #374151;
}
.no-data-sub {
  font-size: 13px;
  color: #9ca3af;
  text-align: center;
  max-width: 300px;
}
.loading-text {
  font-size: 13px;
  color: #9ca3af;
}

/* ─── Search field ─── */
.custom-search :deep(.v-field) {
  border-radius: 10px !important;
  font-size: 13px;
}

/* ─── Table row hover ─── */
.eval-table :deep(tbody tr:hover td) {
  background: #f9fafb !important;
}
.eval-table :deep(th) {
  font-size: 12px !important;
  font-weight: 700 !important;
  color: #6b7280 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  border-bottom: 1px solid #f3f4f6 !important;
  background: #fff !important;
}
.eval-table :deep(td) {
  border-bottom: 1px solid #f9fafb !important;
  font-size: 13.5px;
}
</style>
